/**
 * Store for SubKrewe-collected dues tracking.
 *
 * Entries are keyed by composite `${subkreweId}__${year}__${memberId}` to
 * allow holding multiple years and multiple subkrewes simultaneously in
 * state (admins and members can see different slices).
 *
 * See docs/SubkreweDues.md for product intent — money never flows
 * through Chewbacchus; these records are bookkeeping only.
 */
import { collection, doc, getDoc, onSnapshot, serverTimestamp, setDoc } from 'firebase/firestore'
import { defineStore } from 'pinia'
import type { SubkreweDuesEntry, SubkreweDuesEntryFirestoreData, SubkreweDuesMethod, SubkreweDuesStatus } from '~/shared/types/subkreweDues.js'
import { subkreweDuesEntryKey } from '~/shared/utils/subkreweDues.js'
import type { LiveChange, LiveSpec } from '~/utils/liveData'
import { acquireLive, holdLive, liveDocListener, liveQueryListener } from '~/utils/liveData'

// subkrewes/{subkreweId}/dues_payments/{year}/entries
function entriesPath(subkreweId: string, year: string | number): string {
  return `subkrewes/${subkreweId}/dues_payments/${String(year)}/entries`
}

// The state key of an entry's document path
function entryKey(path: string): string {
  const [, subkreweId, , year, , memberId] = path.split('/')
  return subkreweDuesEntryKey(subkreweId!, year!, memberId!)
}

interface SetEntryInput {
  subkreweId: string
  year: string | number
  memberId: string
  status: SubkreweDuesStatus
  amount_paid_cents?: number
  method?: SubkreweDuesMethod
  note?: string
}

function fromFirestore(data: SubkreweDuesEntryFirestoreData): SubkreweDuesEntry {
  const toDate = (v: unknown): Date => {
    if (v instanceof Date) return v
    if (v && typeof (v as { toDate?: () => Date }).toDate === 'function') {
      return (v as { toDate: () => Date }).toDate()
    }
    return new Date()
  }
  return {
    member_id: data.member_id,
    year: data.year,
    status: data.status,
    amount_paid_cents: data.amount_paid_cents,
    method: data.method,
    note: data.note,
    recorded_by: data.recorded_by,
    recorded_at: toDate(data.recorded_at),
    updated_at: toDate(data.updated_at),
  }
}

export const useSubkreweDuesStore = defineStore('subkreweDues', {
  state: () => ({
    entries: {} as Record<string, SubkreweDuesEntry>,
    loading: false,
  }),

  getters: {
    getEntry(): (subkreweId: string, year: string | number, memberId: string) => SubkreweDuesEntry | undefined {
      return (subkreweId, year, memberId) => this.entries[subkreweDuesEntryKey(subkreweId, year, memberId)]
    },

    /**
     * All entries for a given subkrewe + year (captain / admin view).
     */
    entriesFor(): (subkreweId: string, year: string | number) => SubkreweDuesEntry[] {
      return (subkreweId, year) => {
        const prefix = `${subkreweId}__${String(year)}__`
        return Object.entries(this.entries)
          .filter(([k]) => k.startsWith(prefix))
          .map(([, v]) => v)
      }
    },
  },

  actions: {
    /**
     * Every dues entry for a subkrewe + year, live until the returned
     * function is called: the view that asked calls it when it closes or
     * moves on to another SubKrewe or year.
     */
    subscribeEntriesForSubkrewe(subkreweId: string, year: string | number): () => void {
      return holdLive(this.entriesSpec(subkreweId, year)).release
    },

    /**
     * Every dues entry for a subkrewe + year, live while the caller holds
     * them (the members CSV export). False when they couldn't be read.
     */
    async loadEntriesForSubkrewe(subkreweId: string, year: string | number): Promise<boolean> {
      try {
        await acquireLive(this.entriesSpec(subkreweId, year))
        return true
      }
      catch (err) {
        console.error(`[subkreweDues] loadEntriesForSubkrewe ${subkreweId} ${year}`, err)
        return false
      }
    },

    /**
     * A member's own entry (the member dashboard, which doesn't read the
     * whole SubKrewe's), live while the caller holds it
     */
    async loadOwnEntry(subkreweId: string, year: string | number, memberId: string): Promise<SubkreweDuesEntry | undefined> {
      await acquireLive(this.ownEntrySpec(subkreweId, year, memberId))
      return this.getEntry(subkreweId, year, memberId)
    },

    /**
     * A member's own entry, live while the caller holds it
     */
    subscribeOwnEntry(subkreweId: string, year: string | number, memberId: string): void {
      acquireLive(this.ownEntrySpec(subkreweId, year, memberId)).catch((err) => {
        console.error('[subkreweDues] subscribeOwnEntry error', err)
      })
    },

    entriesSpec(subkreweId: string, year: string | number): LiveSpec<SubkreweDuesEntryFirestoreData> {
      const path = entriesPath(subkreweId, year)
      return {
        key: path,
        covers: path,
        listen: (emit, fail) => onSnapshot(
          collection(useFirestore(), 'subkrewes', subkreweId, 'dues_payments', String(year), 'entries'),
          ...liveQueryListener(path, emit, fail),
        ),
        apply: changes => this.applyEntries(changes),
        evict: paths => this.evictEntries(paths),
      }
    },

    ownEntrySpec(subkreweId: string, year: string | number, memberId: string): LiveSpec<SubkreweDuesEntryFirestoreData> {
      const collectionPath = entriesPath(subkreweId, year)
      const path = `${collectionPath}/${memberId}`
      return {
        key: path,
        within: collectionPath,
        listen: (emit, fail) => onSnapshot(
          doc(useFirestore(), 'subkrewes', subkreweId, 'dues_payments', String(year), 'entries', memberId),
          ...liveDocListener(path, emit, fail),
        ),
        apply: changes => this.applyEntries(changes),
        evict: paths => this.evictEntries(paths),
      }
    },

    applyEntries(changes: LiveChange<SubkreweDuesEntryFirestoreData>[]) {
      this.$patch((state) => {
        for (const { path, data } of changes) state.entries[entryKey(path)] = fromFirestore(data!)
      })
    },

    evictEntries(paths: string[]) {
      this.$patch((state) => {
        for (const path of paths) delete state.entries[entryKey(path)]
      })
    },

    /**
     * Create or update a dues entry. Captains and admins only (enforced
     * by firestore rules). Writes merge onto existing doc; placeholder
     * parent doc at /dues_payments/{year} is created if absent.
     */
    async setEntry(input: SetEntryInput, recordedBy: string): Promise<void> {
      const db = useFirestore()
      const { subkreweId, year, memberId } = input
      // Ensure the intermediate year doc exists so list/read rules remain
      // consistent; this is a simple placeholder.
      const yearDocRef = doc(db, 'subkrewes', subkreweId, 'dues_payments', String(year))
      const yearSnap = await getDoc(yearDocRef)
      if (!yearSnap.exists()) {
        await setDoc(yearDocRef, { year: String(year), updated_at: serverTimestamp() })
      }

      const entryRef = doc(
        db,
        'subkrewes', subkreweId, 'dues_payments', String(year), 'entries', memberId,
      )
      const existing = await getDoc(entryRef)
      const payload: SubkreweDuesEntryFirestoreData = {
        member_id: memberId,
        year: String(year),
        status: input.status,
        amount_paid_cents: input.amount_paid_cents,
        method: input.method,
        note: input.note,
        recorded_by: existing.exists()
          ? ((existing.data() as SubkreweDuesEntryFirestoreData).recorded_by ?? recordedBy)
          : recordedBy,
        recorded_at: existing.exists()
          ? (existing.data() as SubkreweDuesEntryFirestoreData).recorded_at
          : serverTimestamp(),
        updated_at: serverTimestamp(),
      }
      // Strip undefined fields so firestore doesn't reject the payload.
      const cleaned: Record<string, unknown> = {}
      for (const [k, v] of Object.entries(payload)) {
        if (v !== undefined) cleaned[k] = v
      }
      await setDoc(entryRef, cleaned, { merge: true })
    },
  },
})
